import React from 'react';

import {Grid, Stack} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useQueries} from 'react-query';

import API, {Types, VehicleGetProps, useAuth} from '@wheeebo/polaris-api';
import {
  AttitudeCard,
  BatteryCard,
  CircleLoader,
  FlightLogCard,
  GpsCard,
  Head,
  LoadError,
  RangeFinderCard,
  StatusCard,
  SpeedCard,
  useErrorHandler,
} from '@wheeebo/polaris-ui';

import {config} from '@components/Constants';
import VehicleUpdateCard from '@components/pages/vehicle/VehicleUpdateCard';
import SettingsCard from '@components/pages/vehicle/Settings';
import GroupManagementCard from '@components/pages/vehicle/GroupManagementCard';
import LocationCard from '@components/pages/vehicle/LocationCard';

const REALTIME_GEOMETRICS_REFRESH_INTERVAEL_MSEC = 10000; // 10 seconds.
const REALTIME_METRICS_REFRESH_INTERVAEL_MSEC = 30000; // 30 seconds.
const REALTIME_INACTIVE_REFRESH_INTERVAEL_MSEC = 60000; // 60 seconds.

function VehiclePage(): JSX.Element {
  const params = useParams();
  const {account} = useAuth();
  const {showAndEmitJsErrorEvent} = useErrorHandler();
  const [useRealtime, setUseRealtime] = React.useState(true);
  const options: VehicleGetProps = {preload: ['groups', 'owner']};
  const [
    {data, isError, isLoading},
    {data: realtimeGeometrics},
    {data: lastGeometrics},
    {data: realtimeMetrics},
    {data: lastMetric},
  ] = useQueries([
    {
      queryKey: params.vehicleId ? API.Vehicles.getKey(params.vehicleId, options) : '',
      queryFn: () => (params.vehicleId ? API.Vehicles.get(params.vehicleId, options) : undefined),
      enabled: !!params.vehicleId,
      onError: showAndEmitJsErrorEvent,
    },
    {
      queryKey: params.vehicleId ? API.Vehicles.getRealtimeGeometricsKey(params.vehicleId) : '',
      queryFn: () => (params.vehicleId ? API.Vehicles.getRealtimeGeometrics(params.vehicleId) : undefined),
      enabled: !!params.vehicleId,
      onSuccess: (data?: Types.Geometrics[]) => setUseRealtime(!!data?.at(0)?.latitude),
      refetchInterval: useRealtime
        ? REALTIME_GEOMETRICS_REFRESH_INTERVAEL_MSEC
        : REALTIME_INACTIVE_REFRESH_INTERVAEL_MSEC,
    },
    {
      queryKey: params.vehicleId ? API.Vehicles.getLastGeometricsKey(params.vehicleId) : '',
      queryFn: () => (params.vehicleId ? API.Vehicles.getLastGeometrics(params.vehicleId) : undefined),
      enabled: !!params.vehicleId && !useRealtime,
    },
    {
      queryKey: params.vehicleId ? API.Vehicles.getRealtimeMetricsKey(params.vehicleId) : '',
      queryFn: () => (params.vehicleId ? API.Vehicles.getRealtimeMetrics(params.vehicleId) : undefined),
      enabled: !!params.vehicleId,
      onSuccess: (data?: Types.Metrics[]) => setUseRealtime(!!!!data?.at(0)?.battery),
      refetchInterval: useRealtime ? REALTIME_METRICS_REFRESH_INTERVAEL_MSEC : REALTIME_INACTIVE_REFRESH_INTERVAEL_MSEC,
    },
    {
      queryKey: params.vehicleId ? API.Vehicles.getLastMetricsKey(params.vehicleId) : '',
      queryFn: () => (params.vehicleId ? API.Vehicles.getLastMetrics(params.vehicleId) : undefined),
      enabled: !!params.vehicleId && !useRealtime,
    },
  ]);

  return (
    <Grid container spacing={1}>
      <Head title="Vehicle" />
      <Grid item xs={12} sm={4}>
        <Stack gap={1}>
          {isLoading ? <CircleLoader /> : null}
          {isError ? <LoadError /> : null}
          {params.vehicleId ? <VehicleUpdateCard vehicleId={params.vehicleId} /> : null}
          {data?.vehicleType === Types.VehicleTypeDrone && params.vehicleId ? (
            <FlightLogCard apiKey={config.GOOGLE_MAP_API_KEY || ''} vehicleId={params.vehicleId} />
          ) : null}
        </Stack>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {data ? (
              <LocationCard
                apiKey={config.GOOGLE_MAP_API_KEY || ''}
                vehicle={data}
                realtimeMetrics={realtimeGeometrics ?? undefined}
                lastMetrics={lastGeometrics ?? undefined}
              />
            ) : null}
          </Grid>
          <Grid item xs={12} sm={6}>
            {data?.vehicleType === Types.VehicleTypeDrone ? (
              <Stack gap={1}>
                <StatusCard
                  realtimeMetrics={realtimeMetrics}
                  lastMetrics={lastMetric}
                  refreshInterval={REALTIME_METRICS_REFRESH_INTERVAEL_MSEC}
                />
                <BatteryCard
                  realtimeMetrics={realtimeMetrics}
                  lastMetrics={lastMetric}
                  refreshInterval={REALTIME_METRICS_REFRESH_INTERVAEL_MSEC}
                />
                <RangeFinderCard realtimeMetrics={realtimeMetrics} lastMetrics={lastMetric} />
                <GpsCard realtimeMetrics={realtimeMetrics} lastMetrics={lastMetric} />
              </Stack>
            ) : null}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack gap={1}>
              {params.vehicleId ? <GroupManagementCard vehicleId={params.vehicleId} /> : null}
              {data?.vehicleType === Types.VehicleTypeDrone ? (
                <>
                  <AttitudeCard realtimeMetrics={realtimeMetrics} lastMetrics={lastMetric} />
                  <SpeedCard
                    realtimeMetrics={realtimeMetrics}
                    lastMetrics={lastMetric}
                    refreshInterval={REALTIME_METRICS_REFRESH_INTERVAEL_MSEC}
                  />
                  {params.vehicleId && account?.id === data?.owner?.id ? (
                    <SettingsCard vehicleId={params.vehicleId} />
                  ) : null}
                </>
              ) : null}
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default React.memo(VehiclePage);
